<template>
	<div class="thumbnail-weather-map">
		<!-- ローディング -->
		<transition name="fade">
			<webview-redraw-progress v-if="isLoading" />
		</transition>
		<!-- /ローディング -->

		<!-- エラー -->
		<div v-if="isError">
			<common-error-message>データを正常に<br>取得できませんでした</common-error-message>
		</div>
		<!-- /エラー -->

		<!-- 全国マップサムネイル -->
		<canvas v-else class='thumbnail-weather-map__canvas' width="490" height="514" id='js_thumbnail-weather-map__canvas'></canvas>
		<!-- /全国マップサムネイル -->
	</div>
</template>

<script>
// Vuex
import { mapActions, mapGetters } from 'vuex'
import { SET_WEATHER_DATA } from '@/store/modules/weatherMap/mutation-types'

// Compornents
import WebviewRedrawProgress from '@/components/Molecules/WebviewRedrawProgress'
import CommonErrorMessage from '@/components/Molecules/CommonErrorMessage'

// ライブラリ
import cmnConst from '@/assets/js/constant.js'
const createjs = window.createjs

// 定数
const CARD_PROP_ARRAY = {	// 全国カード表示位置
	'01101': { no: 0, cityName: 'sapporo', x: 312, y: 60, isUnder: false },
	'04101': { no: 11, cityName: 'sendai', x: 430, y: 280, isUnder: true },
	'13101': { no: 20, cityName: 'tokyo', x: 370, y: 410, isUnder: true },
	'15103': { no: 13, cityName: 'niigata', x: 265, y: 160, isUnder: false },
	'17201': { no: 24, cityName: 'kanazawa', x: 190, y: 215, isUnder: false },
	'23106': { no: 28, cityName: 'nagoya', x: 295, y: 425, isUnder: true },
	'27128': { no: 34, cityName: 'osaka', x: 225, y: 442, isUnder: true },
	'34101': { no: 39, cityName: 'hiroshima', x: 120, y: 215, isUnder: false },
	'39201': { no: 43, cityName: 'kouchi', x: 150, y: 455, isUnder: true },
	'40133': { no: 45, cityName: 'fukuoka', x: 50, y: 250, isUnder: false },
	'47201': { no: 53, cityName: 'naha', x: 195, y: 90, isUnder: false }
}
const RESOLUTION = '@2x'
const CARD_SIZE = {
	'w_@2x': 92,
	'h_@2x': 130,
	'w_@3x': 138,
	'h_@3x': 195
}

export default {
	name: 'ThumbnailWeatherMap',
	components: {
		WebviewRedrawProgress,
		CommonErrorMessage
	},
	data () {
		return {
			canvas: null,
			celSpriteSheet: null,
			queue: null,
			isError: false,
			isLoading: false
		}
	},

	mounted () {
		this.getData()
	},

	computed: {
		// map Vuex getters
		...mapGetters('weatherMap', ['weatherData'])
	},

	methods: {
		// map Vuex actions
		...mapActions('weatherMap', [SET_WEATHER_DATA]),

		// 画像のプリロード処理
		preload () {
			// マニフェストファイル
			const manifest = [
				{ 'src': `${cmnConst.FRONT_URL}static/weathermap/thumbnail-weather-map_bg${RESOLUTION}.png`, 'id': 'bg' },
				{ 'src': `${cmnConst.FRONT_URL}static/weathermap/cel_sprite${RESOLUTION}.png`, 'id': 'cel_sprite' },
				{ 'src': `${cmnConst.FRONT_URL}static/weathermap/map_kiatu_icon_lv1${RESOLUTION}.png`, 'id': 'map_kiatu_icon_lv1' },
				{ 'src': `${cmnConst.FRONT_URL}static/weathermap/map_kiatu_icon_lv2${RESOLUTION}.png`, 'id': 'map_kiatu_icon_lv2' },
				{ 'src': `${cmnConst.FRONT_URL}static/weathermap/map_kiatu_icon_lv3${RESOLUTION}.png`, 'id': 'map_kiatu_icon_lv3' },
				{ 'src': `${cmnConst.FRONT_URL}static/weathermap/map_kiatu_icon_lv4${RESOLUTION}.png`, 'id': 'map_kiatu_icon_lv4' },
				{ 'src': `${cmnConst.FRONT_URL}static/weathermap/map_kiatu_icon_lv5${RESOLUTION}.png`, 'id': 'map_kiatu_icon_lv5' }
			]

			const _this = this
			return new Promise((resolve, reject) => {
				_this.queue = new createjs.LoadQueue(false)
				_this.queue.setMaxConnections(manifest.length)
				_this.queue.loadManifest(manifest, true)

				// LoadQueueでの画像読み込みが完了したらCanvasの初期化
				_this.queue.addEventListener('complete', e => {
					_this.queue.removeAllEventListeners('complete')
					_this.queue.removeAllEventListeners('fileload')
					resolve('OK')
				})

				// LoadQueueでの画像読み込みが失敗
				_this.queue.addEventListener('error', e => {
					reject(e)
				})
			})
		},

		// 全国マップ生成に必要なデータ取得処理
		getData () {
			const SET_WEATHER_DATA = this.SET_WEATHER_DATA({
				csid: 'mmcm',
				format: 'json'
			})
			const apiArray = [SET_WEATHER_DATA, this.preload()]
			this.isLoading = true
			Promise.all(apiArray).then(res => {
				// console.log('WeatherMap SET_WEATHER_DATA: ', res)
				this.initSprite()
				this.initCanvas()
			}).catch(error => {
				// エラー処理
				console.log(error)
				this.isError = true
			}).finally(() => {
				this.isLoading = false
			})
		},

		// 地名カード画像のスプライト作成
		initSprite () {
			const cardWidth = CARD_SIZE[`w_${RESOLUTION}`]
			const cardHeight = CARD_SIZE[`h_${RESOLUTION}`]
			const maxCityCount = 59	// 地点カードの最大数（地方版含む）

			const celSpriteData = {
				images: [this.queue.getResult('cel_sprite')],
				frames: { width: cardWidth, height: cardHeight, count: maxCityCount, regX: cardWidth / 2, regY: cardHeight / 2, spacing: 0, margin: 0 },
				animations: {}
			}

			for (const prop in CARD_PROP_ARRAY) {
				celSpriteData.animations[CARD_PROP_ARRAY[prop].cityName] = CARD_PROP_ARRAY[prop].no
			}
			this.celSpriteSheet = new createjs.SpriteSheet(celSpriteData)
		},

		// 全国マップの描画処理
		initCanvas () {
			this.canvas = new createjs.Stage('js_thumbnail-weather-map__canvas')
			this.canvas.clear()

			// 背景をステージに追加
			const bg = new createjs.Bitmap(this.queue.getResult('bg'))	// CSSでもエラー用に背景画像を設定しているが、Base64に変換するためCanvasにも背景画像を含める
			this.canvas.addChild(bg)

			// 地名カードと気圧
			for (var i = 0, len = this.weatherData.locations.length; i < len; i++) {
				const data = this.weatherData.locations[i]
				const prop = CARD_PROP_ARRAY[data.chitenCode]

				// コンテナ作成
				const cardContainer = new createjs.Container()
				cardContainer.mouseEnabled = false	// コンテナのマウスイベント停止
				cardContainer.x = prop.x
				cardContainer.y = prop.y
				cardContainer.scaleX = 0.8
				cardContainer.scaleY = 0.8

				// 地名
				const card = new createjs.Sprite(this.celSpriteSheet)
				card.gotoAndStop(prop.cityName)
				cardContainer.addChild(card)

				// 気圧
				let pressure = data.forecasts[0].pressure.level
				if (pressure === '0' || Number(pressure) === 0) pressure = '1'
				const level = new createjs.Bitmap(this.queue.getResult(`map_kiatu_icon_lv${pressure}`))
				level.regX = level.image.width / 2
				level.regY = level.image.height / 2
				level.x = 0
				level.y = prop.isUnder ? -18 : 14	// 位置の微調整
				cardContainer.addChild(level)

				// コンテナをステージに追加
				this.canvas.addChild(cardContainer)
			}

			// 描画アップデート
			this.canvas.update()

			// Base64に変換しemitする
			this.$emit('canvasDataUrl', this.canvas.toDataURL('image/png'))
		}
	}
}
</script>

<style lang='scss' scoped>
@import "@/assets/sass/variable";

.thumbnail-weather-map {
	position: relative;
	width: 100%;
	padding-top: 104.8979%;
	margin: 0 auto;
	background: url(~@/assets/img/thumbnail-weather-map_bg@2x.png) no-repeat 0 0;
	background-size: 100% auto;

	.redraw-progress {
		position: absolute;
		top: 0;
		left: 0;
	}

	canvas {
		position: absolute;
		display: block;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}
}

.fade-enter-active, .fade-leave-active {
	transition: opacity .2s;
}
.fade-enter, .fade-leave-to {
	opacity: 0;
}
</style>
